import { useMemo, useState } from "react";
import { useCustom, useCustomMutation, useGetLocale, useTranslate } from "@refinedev/core";
import {
  PlusCircleSolidIcon,
  Button,
  Field,
  Modal,
  SelectField,
  StatusBadge,
  TextareaField,
  type Column,
  type StatusTone,
} from "@fawkes/ui";

import { formatDateTime } from "../i18n/format";
import { IndeelbareTabel } from "../components/IndeelbareTabel";

// Crematorium (Epic 21, MR-4 read / MR-W5 write): the cremation-runs overview — the React port of the
// Jmix runs view. Lists all cremation runs newest scheduled date/time first. MR-W5 adds create/edit
// (CrematieRunService) via a modal editor; ROLE_CREMATORIUM_VIEW reads, ROLE_CREMATORIUM_EDIT writes
// (server-enforced — a 403 surfaces inline, a 409 prompts a refresh).
//
// The url is RELATIVE ("crematorium/runs") — the dataProvider prepends /api once; a leading slash
// would double to /api/api → 404. Search + the status filter are client-side over the fetched list.

// The run-status vocabulary — stable enum ids, rendered via i18n (crematoriumRuns.status.<ID>).
const RUN_STATUSSEN = [
  "CONCEPT",
  "GEPLAND",
  "IN_UITVOERING",
  "UITGEVOERD",
  "GECONTROLEERD",
  "AFGESLOTEN",
  "GEANNULEERD",
] as const;

const VORMEN = [
  "INDIVIDUEEL",
  "UITGEBREID",
  "COLLECTIEF",
  "RESOMEREN_INDIVIDUEEL",
  "RESOMEREN_COLLECTIEF",
  "BEGRAFENIS_INDIVIDUEEL",
  "BEGRAFENIS_ANONIEM",
  "DESTRUCTIE",
] as const;

interface RunRow {
  id: string;
  runnummer: string | null;
  datumTijd: string | null;
  verwerkingsvorm: string | null;
  locatie: string | null;
  oven: string | null;
  status: string | null;
  capaciteitKg: number | null;
  totaalGewichtKg: number | null;
  opmerkingen: string | null;
  version: number | null;
}

type RunNotice = "conflict" | "forbidden" | "error";

interface RunForm {
  datumTijd: string;
  verwerkingsvorm: string;
  locatie: string;
  oven: string;
  capaciteitKg: string;
  status: string;
  opmerkingen: string;
}

const EMPTY_RUN: RunForm = {
  datumTijd: "",
  verwerkingsvorm: "",
  locatie: "",
  oven: "",
  capaciteitKg: "",
  status: "CONCEPT",
  opmerkingen: "",
};

function toRunForm(r: RunRow): RunForm {
  return {
    datumTijd: r.datumTijd ? r.datumTijd.slice(0, 16) : "",
    verwerkingsvorm: r.verwerkingsvorm ?? "",
    locatie: r.locatie ?? "",
    oven: r.oven ?? "",
    capaciteitKg: r.capaciteitKg != null ? String(r.capaciteitKg) : "",
    status: r.status ?? "CONCEPT",
    opmerkingen: r.opmerkingen ?? "",
  };
}

function num(v: string): number | null {
  if (v.trim() === "") return null;
  const n = Number(v.replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

function statusTone(status: string | null): StatusTone {
  if (status === "UITGEVOERD" || status === "GECONTROLEERD" || status === "AFGESLOTEN") return "success";
  if (status === "IN_UITVOERING" || status === "GEPLAND") return "warning";
  if (status === "GEANNULEERD") return "neutral";
  return "neutral";
}

export function CrematoriumRunsList() {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";

  const { data, isLoading, isError, refetch } = useCustom<RunRow[]>({
    url: "crematorium/runs",
    method: "get",
  });
  const rows = data?.data ?? [];

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const { mutate, isLoading: saving } = useCustomMutation();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<RunRow | null>(null);
  const [form, setForm] = useState<RunForm>(EMPTY_RUN);
  const [notice, setNotice] = useState<RunNotice | null>(null);
  const modalOpen = adding || editing != null;

  const setField = (key: keyof RunForm) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY_RUN);
    setNotice(null);
    setAdding(true);
  };
  const openEdit = (r: RunRow) => {
    setAdding(false);
    setEditing(r);
    setForm(toRunForm(r));
    setNotice(null);
  };
  const close = () => {
    setAdding(false);
    setEditing(null);
    setNotice(null);
  };

  const onError = (err: unknown) => {
    const code = (err as { statusCode?: number }).statusCode;
    setNotice(code === 409 ? "conflict" : code === 403 ? "forbidden" : "error");
  };

  const save = () => {
    setNotice(null);
    const isEdit = editing != null;
    const values = {
      datumTijd: form.datumTijd || null,
      verwerkingsvorm: form.verwerkingsvorm || null,
      locatie: form.locatie || null,
      oven: form.oven || null,
      capaciteitKg: num(form.capaciteitKg),
      status: form.status || null,
      opmerkingen: form.opmerkingen || null,
      version: editing?.version ?? null,
    };
    mutate(
      {
        url: isEdit ? `crematorium/runs/${editing.id}` : "crematorium/runs",
        method: isEdit ? "put" : "post",
        values,
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => {
          close();
          refetch();
        },
        onError,
      },
    );
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFilter && r.status !== statusFilter) return false;
      if (q) {
        const hay = `${r.runnummer ?? ""} ${r.locatie ?? ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, search, statusFilter]);

  const columns: Column<RunRow>[] = [
    { header: t("crematoriumRuns.col.runnummer"), cell: (r) => r.runnummer ?? "—" },
    {
      header: t("crematoriumRuns.col.datumTijd"),
      cell: (r) => formatDateTime(r.datumTijd, locale),
    },
    {
      header: t("crematoriumRuns.col.vorm"),
      cell: (r) => (r.verwerkingsvorm ? t(`crematoriumTarief.vorm.${r.verwerkingsvorm}`) : "—"),
    },
    { header: t("crematoriumRuns.col.locatie"), cell: (r) => r.locatie ?? "—" },
    { header: t("crematoriumRuns.col.oven"), cell: (r) => r.oven ?? "—" },
    {
      header: t("crematoriumRuns.col.status"),
      cell: (r) => (
        <StatusBadge tone={statusTone(r.status)}>
          {r.status ? t(`crematoriumRuns.status.${r.status}`) : "—"}
        </StatusBadge>
      ),
    },
    {
      header: t("crematoriumRuns.col.totaalGewicht"),
      align: "right",
      cell: (r) => (r.totaalGewichtKg != null ? `${r.totaalGewichtKg} kg` : "—"),
    },
    {
      header: t("crematoriumRuns.col.acties"),
      align: "right",
      cell: (r) => (
        <Button variant="secondary" onClick={() => openEdit(r)}>
          {t("crematoriumRuns.action.bewerken")}
        </Button>
      ),
    },
  ];

  return (
    <div className="product-overview">
      <div className="asiel-tarief__panel">
        <div className="asiel-tarief__toolbar">
          <Field
            label={t("crematoriumRuns.search.label")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <SelectField
            label={t("crematoriumRuns.filter.status")}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            options={[
              { value: "", label: t("crematoriumRuns.filter.all") },
              ...RUN_STATUSSEN.map((s) => ({ value: s, label: t(`crematoriumRuns.status.${s}`) })),
            ]}
          />
          <span className="asiel-tarief__toolbar-spacer" />
          <Button variant="primary" onClick={openAdd}>
            <PlusCircleSolidIcon width={18} height={18} /> {t("crematoriumRuns.action.nieuw")}
          </Button>
          <Button variant="secondary" onClick={() => refetch()}>
            {t("crematoriumRuns.action.refresh")}
          </Button>
        </div>

        {isError && (
          <div className="asiel-tarief__notice asiel-tarief__notice--error">
            {t("crematoriumRuns.load.error")}
          </div>
        )}

        {notice && !modalOpen && <p className="fawkes-error">{t(`crematoriumRuns.${notice}`)}</p>}

        <IndeelbareTabel<RunRow> tabelKey="crematorium-runs-list"
          columns={columns}
          rows={filtered}
          getRowKey={(r) => r.id}
          empty={isLoading ? t("common.loading") : t("crematoriumRuns.empty")}
        />
      </div>

      <Modal
        open={modalOpen}
        title={t(editing ? "crematoriumRuns.edit.title" : "crematoriumRuns.add.title")}
        onClose={close}
        footer={
          <>
            <Button variant="secondary" disabled={saving} onClick={close}>
              {t("crematoriumRuns.action.annuleren")}
            </Button>
            <Button variant="primary" disabled={saving} onClick={save}>
              {t("crematoriumRuns.action.opslaan")}
            </Button>
          </>
        }
      >
        <div className="crem-detail__grid">
          <Field
            label={t("crematoriumRuns.veld.datumTijd")}
            type="datetime-local"
            value={form.datumTijd}
            onChange={setField("datumTijd")}
          />
          <SelectField
            label={t("crematoriumRuns.veld.vorm")}
            value={form.verwerkingsvorm}
            onChange={setField("verwerkingsvorm")}
            options={[
              { value: "", label: "—" },
              ...VORMEN.map((v) => ({ value: v, label: t(`crematoriumTarief.vorm.${v}`) })),
            ]}
          />
          <Field label={t("crematoriumRuns.veld.locatie")} value={form.locatie} onChange={setField("locatie")} />
          <Field label={t("crematoriumRuns.veld.oven")} value={form.oven} onChange={setField("oven")} />
          <Field
            label={t("crematoriumRuns.veld.capaciteit")}
            type="number"
            value={form.capaciteitKg}
            onChange={setField("capaciteitKg")}
          />
          <SelectField
            label={t("crematoriumRuns.veld.status")}
            value={form.status}
            onChange={setField("status")}
            options={RUN_STATUSSEN.map((s) => ({ value: s, label: t(`crematoriumRuns.status.${s}`) }))}
          />
        </div>
        <TextareaField
          label={t("crematoriumRuns.veld.opmerkingen")}
          rows={2}
          value={form.opmerkingen}
          onChange={setField("opmerkingen")}
        />
        {notice && <p className="fawkes-error">{t(`crematoriumRuns.${notice}`)}</p>}
      </Modal>
    </div>
  );
}
